import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Space,
  Input,
  Select,
  Button,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  message,
  Typography,
  Spin,
} from "antd";
import {
  SearchOutlined,
  ShopOutlined,
  EditOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  saasControlService,
  SaasCustomer,
  SubscriptionTier,
  SubscriptionStatus,
  UpdateClientSubscriptionRequest,
} from "~/services/saas-control";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Text } = Typography;
const { Option } = Select;

export const CustomersTab: React.FC = () => {
  const [customers, setCustomers] = useState<SaasCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<string | undefined>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<SaasCustomer | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await saasControlService.getSaasCustomers();
      setCustomers(data);
    } catch (error) {
      console.error("Failed to load customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer: SaasCustomer) => {
    setEditingCustomer(customer);
    form.setFieldsValue({
      subscriptionTier: customer.subscriptionTier,
      subscriptionStatus: customer.subscriptionStatus,
      subscriptionStartDate: customer.subscriptionStartDate
        ? dayjs(customer.subscriptionStartDate)
        : undefined,
      subscriptionEndDate: customer.subscriptionEndDate
        ? dayjs(customer.subscriptionEndDate)
        : undefined,
      monthlyServiceSpend: customer.monthlyServiceSpend,
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    if (!editingCustomer) return;

    try {
      setSubmitting(true);
      const data: UpdateClientSubscriptionRequest = {
        ...values,
        subscriptionStartDate: values.subscriptionStartDate?.toISOString(),
        subscriptionEndDate: values.subscriptionEndDate?.toISOString(),
      };

      await saasControlService.updateClientSubscription(
        editingCustomer.id,
        data
      );
      message.success("Subscription updated successfully");
      setIsModalVisible(false);
      loadCustomers();
    } catch (error) {
      message.error("Failed to update subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const getTierColor = (tier: SubscriptionTier) => {
    switch (tier) {
      case SubscriptionTier.FREE:
        return "default";
      case SubscriptionTier.BASIC:
        return "blue";
      case SubscriptionTier.PREMIUM:
        return "gold";
      case SubscriptionTier.ENTERPRISE:
        return "purple";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return "green";
      case SubscriptionStatus.TRIAL:
        return "blue";
      case SubscriptionStatus.CANCELLED:
        return "red";
      case SubscriptionStatus.EXPIRED:
        return "orange";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<SaasCustomer> = [
    {
      title: "Company",
      key: "company",
      render: (_, record) => (
        <Space>
          <ShopOutlined />
          <div>
            <div className="font-medium">{record.companyName}</div>
            <Text type="secondary" className="text-xs">
              {record.email || "No email"}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Tier",
      dataIndex: "subscriptionTier",
      key: "subscriptionTier",
      render: (tier) => (
        <Tag color={getTierColor(tier)}>{tier.toUpperCase()}</Tag>
      ),
      filters: Object.values(SubscriptionTier).map((tier) => ({
        text: tier.charAt(0).toUpperCase() + tier.slice(1),
        value: tier,
      })),
      onFilter: (value, record) => record.subscriptionTier === value,
    },
    {
      title: "Status",
      dataIndex: "subscriptionStatus",
      key: "subscriptionStatus",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Monthly Spend",
      dataIndex: "monthlyServiceSpend",
      key: "monthlyServiceSpend",
      render: (amount) => (
        <Text>
          <DollarOutlined /> {Number(amount).toFixed(2)}
        </Text>
      ),
      sorter: (a, b) =>
        Number(a.monthlyServiceSpend) - Number(b.monthlyServiceSpend),
    },
    {
      title: "Total Payments",
      dataIndex: "totalPayments",
      key: "totalPayments",
      render: (amount) => (
        <Text type="success" strong>
          ${Number(amount).toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.totalPayments - b.totalPayments,
    },
    {
      title: "Last Payment",
      dataIndex: "lastPaymentDate",
      key: "lastPaymentDate",
      render: (date) =>
        date ? (
          <div>
            <div>{dayjs(date).format("MMM D, YYYY")}</div>
            <Text type="secondary" className="text-xs">
              {dayjs(date).fromNow()}
            </Text>
          </div>
        ) : (
          <Text type="secondary">Never</Text>
        ),
    },
    {
      title: "Customer Since",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("MMM D, YYYY"),
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const filteredCustomers = customers.filter((customer) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      customer.companyName.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower);

    const matchesTier =
      tierFilter === undefined || customer.subscriptionTier === tierFilter;

    return matchesSearch && matchesTier;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex gap-4">
        <Input
          placeholder="Search customers..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Select
          placeholder="Filter by tier"
          value={tierFilter}
          onChange={setTierFilter}
          allowClear
          style={{ width: 150 }}
        >
          {Object.values(SubscriptionTier).map((tier) => (
            <Option key={tier} value={tier}>
              {tier.charAt(0).toUpperCase() + tier.slice(1)}
            </Option>
          ))}
        </Select>
      </div>

      <Table
        columns={columns}
        dataSource={filteredCustomers}
        rowKey="id"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} customers`,
        }}
      />

      <Modal
        title={`Edit Subscription - ${editingCustomer?.companyName}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="subscriptionTier" label="Subscription Tier">
            <Select>
              {Object.values(SubscriptionTier).map((tier) => (
                <Option key={tier} value={tier}>
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="subscriptionStatus" label="Status">
            <Select>
              {Object.values(SubscriptionStatus).map((status) => (
                <Option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Space style={{ width: "100%" }} size="large">
            <Form.Item
              name="subscriptionStartDate"
              label="Start Date"
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="subscriptionEndDate"
              label="End Date"
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Space>

          <Form.Item name="monthlyServiceSpend" label="Monthly Service Spend">
            <InputNumber
              prefix="$"
              min={0}
              precision={2}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Update
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
